import React, { useState, useMemo, useEffect } from 'react';
import {
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Avatar from '@material-ui/core/Avatar';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Chip from '@material-ui/core/Chip';
import InputBase from '@material-ui/core/InputBase';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import SecurityIcon from '@material-ui/icons/Security';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { SkeletonLoader } from '../../components/loading/SkeletonLoader';
import { Button, Paper } from '@material-ui/core';

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: theme.palette.grey[100],
    },

    // ── Header ──
    templateHeader: {
      backgroundColor: '#fff',
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1.25, 2),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.25),
      flexShrink: 0,
    },
    backButton: {
      color: theme.palette.text.secondary,
      padding: 6,
      // Same small nudge-left on hover as the Enquiry form's header back link (headerBackLink in enquiryFormDrawerStyles.ts).
      '& .MuiSvgIcon-root': {
        transition: 'transform 0.15s ease',
      },
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.primary,
        '& .MuiSvgIcon-root': {
          transform: 'translateX(-2px)',
        },
      },
      '@media (prefers-reduced-motion: reduce)': {
        '&:hover .MuiSvgIcon-root': {
          transform: 'none',
        },
      },
    },
    headerDivider: {
      width: 1,
      height: 28,
      backgroundColor: theme.palette.divider,
      marginRight: theme.spacing(0.25),
      flexShrink: 0,
    },
    templateIcon: {
      width: 36,
      height: 36,
      borderRadius: 8,
      backgroundColor: '#e3f2fd',
      color: '#1565c0',
      flexShrink: 0,
    },
    headerText: {
      flex: 1,
      minWidth: 0,
    },
    templateTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: theme.palette.text.primary,
      lineHeight: 1.3,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    templateSubtitle: {
      fontSize: 11,
      color: theme.palette.text.secondary,
      marginTop: 1,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    headerActionButton: {
      textTransform: 'none' as const,
      fontSize: 12,
      fontWeight: 500,
      borderRadius: 20,
      padding: '3px 12px',
      backgroundColor: 'rgba(21, 101, 192, 0.07)',
      color: theme.palette.primary.dark,
      flexShrink: 0,
      '&:hover': {
        backgroundColor: 'rgba(21, 101, 192, 0.14)',
      },
    },

    // ── Filter bar (above sidebar+main) ──
    filterBar: {
      backgroundColor: '#fff',
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 2),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      flexShrink: 0,
      flexWrap: 'wrap' as const,
    },
    filterLabel: {
      fontSize: 11,
      fontWeight: 600,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.06em',
      color: theme.palette.text.disabled,
      marginRight: theme.spacing(0.5),
      whiteSpace: 'nowrap' as const,
    },
    filterChip: {
      height: 28,
      fontSize: 12,
      fontWeight: 500,
      borderRadius: 20,
      border: `1.5px solid ${theme.palette.grey[300]}`,
      backgroundColor: '#fff !important',
      color: theme.palette.text.secondary,
      cursor: 'pointer',
      '&:hover': {
        borderColor: '#1565c0',
        color: '#1565c0',
        backgroundColor: '#e3f2fd',
      },
      '& .MuiChip-label': { paddingLeft: 10, paddingRight: 10 },
    },
    filterChipActive: {
      height: 28,
      fontSize: 12,
      fontWeight: 500,
      borderRadius: 20,
      border: `1.5px solid #1565c0`,
      backgroundColor: '#1565c0 !important',
      color: '#fff',
      cursor: 'pointer',
      '&:hover': { backgroundColor: '#003c8f' },
      '& .MuiChip-label': { paddingLeft: 10, paddingRight: 10 },
      '& .MuiChip-icon': {
        color: '#fff',
      },
      '& .MuiChip-deleteIcon': {
        color: 'rgba(255,255,255,0.7)',
        '&:hover': { color: '#fff' },
      },
    },
    filterDivider: {
      width: 1,
      height: 24,
      backgroundColor: theme.palette.divider,
      margin: theme.spacing(0, 0.5),
      flexShrink: 0,
    },
    filterSpacer: { flex: 1 },
    systemToggle: {
      height: 28,
      fontSize: 12,
      fontWeight: 500,
      borderRadius: 20,
      border: `1.5px solid ${theme.palette.grey[300]}`,
      backgroundColor: '#fff',
      color: theme.palette.text.secondary,
      cursor: 'pointer',
      whiteSpace: 'nowrap' as const,
      '&:hover': { borderColor: '#f59e0b', color: '#b45309' },
      '& .MuiChip-label': {
        paddingLeft: 10,
        paddingRight: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
      },
    },
    systemToggleActive: {
      height: 28,
      fontSize: 12,
      fontWeight: 500,
      borderRadius: 20,
      border: `1.5px solid #f59e0b`,
      backgroundColor: '#fffbeb',
      color: '#b45309',
      cursor: 'pointer',
      whiteSpace: 'nowrap' as const,
      '& .MuiChip-label': {
        paddingLeft: 10,
        paddingRight: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
      },
    },

    // ── Body ──
    body: {
      display: 'flex',
      flex: 1,
      overflow: 'hidden',
    },

    // ── Sidebar ──
    sidebar: {
      width: 260,
      flexShrink: 0,
      backgroundColor: '#fff',
      borderRight: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },

    // Sidebar search (inside sidebar, optional)
    sidebarSearch: {
      padding: theme.spacing(1, 1.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    searchWrap: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      backgroundColor: theme.palette.grey[50],
      border: `1px solid ${theme.palette.grey[200]}`,
      borderRadius: 6,
      padding: theme.spacing(0.75, 1.25),
      transition: 'border-color 0.15s, box-shadow 0.15s',
      '&:focus-within': {
        borderColor: '#1565c0',
        boxShadow: '0 0 0 2px rgba(21,101,192,0.12)',
      },
    },
    searchIcon: { fontSize: 15, color: theme.palette.text.disabled, flexShrink: 0 },
    searchInput: { flex: 1, fontSize: 13, '& input': { padding: 0 } },
    clearIcon: {
      fontSize: 14,
      color: theme.palette.text.disabled,
      cursor: 'pointer',
      '&:hover': { color: theme.palette.text.primary },
    },

    // Create button row (shown when search is disabled but onCreate is provided)
    sidebarCreateRow: {
      padding: theme.spacing(1, 1.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
      flexShrink: 0,
    },
    createButton: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(0.75),
      padding: theme.spacing(0.75, 1.25),
      borderRadius: 6,
      border: `1.5px dashed ${theme.palette.grey[300]}`,
      backgroundColor: '#fff',
      color: theme.palette.text.secondary,
      fontSize: 12,
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.12s',
      '&:hover': {
        borderColor: '#1565c0',
        borderStyle: 'solid',
        color: '#1565c0',
        backgroundColor: '#e3f2fd',
      },
    },
    createIconButton: {
      color: '#1565c0',
      padding: 4,
      flexShrink: 0,
      '&:hover': { backgroundColor: '#e3f2fd' },
    },

    sidebarCount: {
      padding: theme.spacing(0.75, 1.5, 0.5),
      fontSize: 11,
      color: theme.palette.text.disabled,
      fontWeight: 500,
      flexShrink: 0,
    },
    sidebarList: {
      flex: 1,
      overflowY: 'auto' as const,
      '&::-webkit-scrollbar': { width: 4 },
      '&::-webkit-scrollbar-thumb': { background: theme.palette.grey[200], borderRadius: 2 },
    },

    // ── Sidebar group ──
    groupHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.75),
      padding: theme.spacing(1.25, 1.5, 0.5),
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.08em',
      color: theme.palette.text.disabled,
      position: 'sticky' as const,
      top: 0,
      backgroundColor: '#fff',
      borderBottom: `1px solid ${theme.palette.grey[100]}`,
      zIndex: 1,
    },
    groupIcon: { fontSize: 13 },

    // ── Sidebar items ──
    sidebarItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1.25),
      padding: theme.spacing(1.25, 1.5),
      cursor: 'pointer',
      borderLeft: '3px solid transparent',
      transition: 'all 0.12s',
      position: 'relative' as const,
      '&:hover': { backgroundColor: theme.palette.grey[50] },
      '&:hover $itemActions': { opacity: 1 },
    },
    sidebarItemActive: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1.25),
      padding: theme.spacing(1.25, 1.5),
      cursor: 'pointer',
      borderLeft: '3px solid #1565c0',
      backgroundColor: '#e3f2fd',
      transition: 'all 0.12s',
      position: 'relative' as const,
      '&:hover $itemActions': { opacity: 1 },
    },
    itemIconWrap: {
      width: 30,
      height: 30,
      borderRadius: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      marginTop: 1,
    },
    itemName: {
      fontSize: 13,
      fontWeight: 500,
      color: theme.palette.text.primary,
      whiteSpace: 'nowrap' as const,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    itemNameActive: {
      fontSize: 13,
      fontWeight: 500,
      color: '#1565c0',
      whiteSpace: 'nowrap' as const,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    itemDesc: {
      fontSize: 11,
      color: theme.palette.text.disabled,
      lineHeight: 1.4,
      marginTop: 2,
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical' as const,
    },
    itemTagsRow: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: 4,
      marginTop: 6,
    },
    itemStatusChip: {
      height: 20,
      borderRadius: 12,
      maxWidth: 90,
      '& .MuiChip-label': {
        display: 'block',
        paddingLeft: 7,
        paddingRight: 7,
        fontSize: 10,
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: 'nowrap' as const,
      },
    },
    itemStatusChipActive: {
      backgroundColor: '#e8f5e9',
      color: '#2e7d32',
      border: '1px solid #a5d6a7',
    },
    itemStatusChipInactive: {
      backgroundColor: '#ffebee',
      color: '#c62828',
      border: '1px solid #ef9a9a',
    },
    itemTagChip: {
      height: 20,
      borderRadius: 12,
      maxWidth: 120,
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.text.secondary,
      border: `1px solid ${theme.palette.grey[300]}`,
      '& .MuiChip-label': {
        display: 'block',
        paddingLeft: 7,
        paddingRight: 7,
        fontSize: 10,
        fontWeight: 500,
        lineHeight: 1,
        maxWidth: 104,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as const,
      },
    },
    itemTagMoreChip: {
      height: 20,
      borderRadius: 12,
      backgroundColor: theme.palette.grey[50],
      color: theme.palette.text.secondary,
      border: `1px dashed ${theme.palette.grey[400]}`,
      cursor: 'default',
      '& .MuiChip-label': {
        paddingLeft: 7,
        paddingRight: 7,
        fontSize: 10,
        fontWeight: 600,
        lineHeight: 1,
      },
    },
    systemBadge: {
      width: 16,
      height: 16,
      borderRadius: '50%',
      backgroundColor: '#fffbeb',
      border: '1px solid #f59e0b',
      color: '#b45309',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 9,
      fontWeight: 700,
      flexShrink: 0,
      marginTop: 6,
    },
    // Edit/Delete action buttons — fade in on row hover
    itemActions: {
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      flexShrink: 0,
      // opacity: 0,
      transition: 'opacity 0.12s',
      marginTop: -2,
    },
    itemActionButton: {
      padding: 5,
      color: theme.palette.text.disabled,
      '&:hover': {
        backgroundColor: '#fff',
        color: theme.palette.text.primary,
      },
    },
    itemActionButtonDelete: {
      padding: 5,
      color: theme.palette.text.disabled,
      '&:hover': {
        backgroundColor: '#fdecea',
        color: '#d32f2f',
      },
    },
    noResults: {
      padding: theme.spacing(3, 2),
      textAlign: 'center' as const,
      color: theme.palette.text.disabled,
      fontSize: 13,
    },

    // ── Main ──
    main: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundColor: theme.palette.grey[50],
    },
    contentControl: {
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
      boxShadow: '0 2px 4px rgba(0,0,0,0.07)',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      padding: theme.spacing(1, 1.5),
      minHeight: '45.6667px'
    },
    contentControlIconWrap: {
      width: 32,
      height: 32,
      borderRadius: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    contentControlText: {
      minWidth: 0,
      flex: 1,
    },
    contentControlTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: theme.palette.text.primary,
      lineHeight: 1.3,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    contentControlSubtitle: {
      fontSize: 11,
      color: theme.palette.text.secondary,
      marginTop: 1,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    contentControlIconBtn: {
      padding: 4,
      borderRadius: 6,
      color: '#70d389',
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
      },
    },
    contentControlAction: {
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    mainContent: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      overflow: 'auto',
      padding: '20px 20px',
    },
    // Same rounded-card framing as rv-grid-container (used for Task/Enquiry grids), but
    // without its overflow:hidden/max-height:100% — tab content here is form-based and
    // needs to grow taller than the viewport and scroll via mainContent's own overflow,
    // not be clipped like a self-scrolling ag-grid would be.
    contentCard: {
      overflowY: 'auto' as const,
      '&::-webkit-scrollbar': { width: 4 },
      '&::-webkit-scrollbar-thumb': { background: theme.palette.grey[200], borderRadius: 2 },
      flex: 1,
      borderRadius: 8,
      boxShadow: '0 1px 6px rgba(0,0,0,0.14)',
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      padding: '15px',
      minHeight: '100%',
    },
  })
);

// ─── Types ───────────────────────────────────────────────────────────────────

export interface SidebarItem {
  id: string;
  name: string;
  description?: string;
  /** Matches a SidebarGroup key and a FilterChip key */
  group: string;
  icon: React.ReactNode;
  iconBg?: string;
  iconColor?: string;
  /** Shown only when showSystemToggle is enabled and toggled on */
  isSystem?: boolean;
  tags?: string[];

  onLearnMore?: () => void;
  info?: string;
  isActive?: boolean;
}

export interface SidebarGroup {
  key: string;
  label: string;
  icon: React.ReactNode;
  iconColor?: string;
}

export interface FilterChip {
  key: string;
  label: string;
  icon?: React.ReactNode;
}

export interface SidebarActions {
  onCreate?: () => void;
  /** Label for the create button (only used when search is disabled and a dedicated row is shown). @default "Create new" */
  createLabel?: string;
  /** Called when the user clicks the edit icon on a sidebar item. If omitted, no edit icon is rendered. */
  onEdit?: (item: SidebarItem) => void;
  /** Called when the user clicks the delete icon on a sidebar item. If omitted, no delete icon is rendered. */
  onDelete?: (item: SidebarItem) => void;
}


export interface ContentControl {
  title?: string;
  subtitle?: string;
  /** Rendered in the same rounded-square treatment as the sidebar item's own icon */
  icon?: React.ReactNode;
  iconBg?: string;
  iconColor?: string;
  actions?: Pick<SidebarActions, 'onCreate' | 'onEdit' | 'onDelete'>;
  loading?: boolean;
}

export interface FilteredVerticalTabPageProps {
  // ── Header ──
  /** Icon shown in the header avatar */
  icon?: React.ReactElement;
  /** Main page title — omit to hide the header entirely */
  title?: string;
  /** Optional subtitle */
  subtitle?: string;
  /** If provided, renders a back arrow */
  onBack?: () => void;
  /** Tooltip for the back button (default: "Back") */
  backTooltip?: string;
  /** If provided, renders a "Learn More" pill button at the end of the header, matching PageContentLayout's pageHeader */
  onLearnMore?: () => void;

  // ── Filter bar ──
  /**
   * Enable the filter chip bar above the sidebar + main area.
   * Requires `filterChips` to be provided.
   * @default false
   */
  enableFilter?: boolean;
  /** Filter chip definitions. Only used when `enableFilter` is true. */
  filterChips?: FilterChip[];
  /** Show the "Include system templates" toggle in the filter bar. @default false */
  showSystemToggle?: boolean;
  /** Label for the system toggle. @default "Include system templates" */
  systemToggleLabel?: string;

  // ── Sidebar search ──
  /**
   * Enable the search input inside the sidebar.
   * @default false
   */
  enableSearch?: boolean;
  /** Placeholder for the sidebar search input. @default "Search…" */
  searchPlaceholder?: string;

  // ── Sidebar ──
  /** Group header definitions */
  sidebarGroups: SidebarGroup[];
  /** All sidebar items */
  sidebarItems: SidebarItem[];
  /** Optionally pre-select an item by id */
  defaultSelectedId?: string;
  /** Controlled selected item id. If provided, parent controls active selection. */
  selectedId?: string | null;
  /** Show skeleton rows in the sidebar while items are loading */
  sidebarLoading?: boolean;
  /** Called when the user selects a sidebar item — use to sync URL or external state */
  onItemSelect?: (item: SidebarItem) => void;

  /** Sidebar-only actions and controls. */
  sidebar?: SidebarActions;

  contentControl?: ContentControl;

  // ── Main content ──
  /**
   * Render function for the main area.
   * Receives the currently selected item (or null if none selected).
   */
  renderContent: (selectedItem: SidebarItem | null) => React.ReactNode;

  hasSharedComponents?: boolean;

  sidebarWidth?: number;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const FilteredVerticalTabPage: React.FC<
  FilteredVerticalTabPageProps
> = ({
  icon,
  title,
  subtitle,
  onBack,
  backTooltip = 'Back',
  onLearnMore,

  enableFilter = false,
  filterChips = [],
  showSystemToggle = false,
  systemToggleLabel = 'Include system templates',

  enableSearch = false,
  searchPlaceholder = 'Search…',

  sidebarGroups,
  sidebarItems,
  defaultSelectedId,
  selectedId,
  sidebarLoading = false,
  onItemSelect,

  sidebar,

  renderContent,
  contentControl,
  hasSharedComponents = false,

  sidebarWidth = 260
}) => {
  const classes = useStyles();

  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  const [showSystem, setShowSystem] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(defaultSelectedId ?? null);
  const isSelectionControlled = selectedId !== undefined;
  const activeSelectedId = isSelectionControlled ? (selectedId ?? null) : internalSelectedId;

  useEffect(() => {
    if (!sidebarItems.length) {
      if (!isSelectionControlled) {
        setInternalSelectedId(null);
      }
      return;
    }

    const hasActiveSelection = !!activeSelectedId && sidebarItems.some((item) => item.id === activeSelectedId);
    if (hasActiveSelection) {
      return;
    }

    const hasDefaultSelection = !!defaultSelectedId && sidebarItems.some((item) => item.id === defaultSelectedId);
    const fallbackId = hasDefaultSelection ? defaultSelectedId! : sidebarItems[0].id;
    const fallbackItem = sidebarItems.find((item) => item.id === fallbackId);

    if (!fallbackItem) {
      return;
    }

    if (!isSelectionControlled) {
      setInternalSelectedId(fallbackItem.id);
    }

    onItemSelect?.(fallbackItem);
  }, [sidebarItems, defaultSelectedId, activeSelectedId, isSelectionControlled, onItemSelect]);

  const toggleFilter = (key: string) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (key === 'all') {
        return next.has('all') ? new Set() : new Set(['all']);
      }
      next.delete('all');
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const uniqueSidebarGroups = useMemo(() => {
    const seenGroupKeys = new Set<string>();
    return sidebarGroups.filter((group) => {
      if (seenGroupKeys.has(group.key)) {
        return false;
      }
      seenGroupKeys.add(group.key);
      return true;
    });
  }, [sidebarGroups]);

  // Filtered + searched items
  const visibleItems = useMemo(() => {
    const lowerSearchQuery = enableSearch ? searchQuery.trim().toLowerCase() : '';
    return sidebarItems.filter(item => {
      if (item.isSystem && !showSystem) return false;
      const matchesFilter =
        !enableFilter ||
        activeFilters.size === 0 ||
        activeFilters.has('all') ||
        activeFilters.has(item.group);
      const matchesSearch =
        !lowerSearchQuery ||
        item.name.toLowerCase().includes(lowerSearchQuery) ||
        (item.description ?? '').toLowerCase().includes(lowerSearchQuery) ||
        (item.tags ?? []).some((tag) => tag.toLowerCase().includes(lowerSearchQuery));
      return matchesFilter && matchesSearch;
    });
  }, [sidebarItems, activeFilters, showSystem, searchQuery, enableFilter, enableSearch]);

  const groupedItems = useMemo(() =>
    uniqueSidebarGroups
      .map(group => ({
        group,
        items: visibleItems.filter(i => i.group === group.key && !i.isSystem),
      }))
      .filter(g => g.items.length > 0),
    [uniqueSidebarGroups, visibleItems]
  );

  const systemItems = useMemo(
    () => visibleItems.filter(i => i.isSystem),
    [visibleItems]
  );

  const selectedItem = sidebarItems.find(i => i.id === activeSelectedId) ?? null;

  // Only show count row when search is enabled (it becomes meaningful context)
  const showCount = enableSearch;

  const onCreate = sidebar?.onCreate;
  const createLabel = sidebar?.createLabel ?? 'Create new';
  const onEdit = sidebar?.onEdit;
  const onDelete = sidebar?.onDelete;

  const hasContentControl = Boolean(contentControl?.title || contentControl?.subtitle || contentControl?.actions);

  // Shared row renderer for both grouped and system items
  const renderItemRow = (item: SidebarItem) => {
    const isSelected = item.id === activeSelectedId;
    const hasActivityState = typeof item.isActive === 'boolean';
    const hasItemInfo = Boolean(item.info && item.info.trim());
    const hasRowActions = Boolean(onEdit || onDelete || item.onLearnMore || hasItemInfo);
    const cappedTags = (item.tags ?? []).slice(0, 5);
    const visibleTags = cappedTags.slice(0, 2);
    const remainingTags = cappedTags.slice(2);

    return (
      <div
        key={item.id}
        className={isSelected ? classes.sidebarItemActive : classes.sidebarItem}
        onClick={() => {
          if (!isSelectionControlled) {
            setInternalSelectedId(item.id);
          }
          onItemSelect?.(item);
        }}
      >
        <div
          className={classes.itemIconWrap}
          style={{
            backgroundColor: item.iconBg ?? (item.isSystem ? '#fffbeb' : '#e3f2fd'),
            color: item.iconColor ?? (item.isSystem ? '#b45309' : '#1565c0'),
          }}
        >
          {item.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0, opacity: item.isActive === false ? 0.75 : 1 }}>
          <Tooltip title={item.name} enterDelay={1000} enterNextDelay={1000}>
            <div className={isSelected ? classes.itemNameActive : classes.itemName}>
              {item.name}
            </div>
          </Tooltip>
          {item.description && (
            <Tooltip title={item.description} enterDelay={1000} enterNextDelay={1000}>
              <div className={classes.itemDesc}>{item.description}</div>
            </Tooltip>
          )}
          {(hasActivityState || visibleTags.length > 0) && (
            <div className={classes.itemTagsRow}>
              {hasActivityState && (
                <Chip
                  label={item.isActive ? 'Active' : 'Inactive'}
                  size="small"
                  className={`${classes.itemStatusChip} ${item.isActive ? classes.itemStatusChipActive : classes.itemStatusChipInactive}`}
                />
              )}
              {visibleTags.map((tag) => (
                  <Tooltip key={`${item.id}-${tag}`} title={tag} enterDelay={500} enterNextDelay={500}>
                    <Chip
                      label={tag}
                      size="small"
                      className={classes.itemTagChip}
                    />
                  </Tooltip>
              ))}
                {remainingTags.length > 0 && (
                  <Tooltip
                    title={remainingTags.join(', ')}
                    enterDelay={300}
                    enterNextDelay={300}
                  >
                    <Chip
                      label={`+${remainingTags.length} more`}
                      size="small"
                      className={classes.itemTagMoreChip}
                    />
                  </Tooltip>
                )}
            </div>
          )}
        </div>

        {/* Edit / Delete — only rendered when the corresponding handler is provided */}
        {hasRowActions && (
          <div
            className={classes.itemActions}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            onTouchStart={e => e.stopPropagation()}
          >
            {item.onLearnMore && (
              <Tooltip title="Learn more">
                <IconButton
                  size="small"
                  className={classes.itemActionButton}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    item.onLearnMore?.();
                  }}
                  aria-label={`Learn more about ${item.name}`}
                >
                  <VideoLibraryIcon style={{ fontSize: 15 }} color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {hasItemInfo && (
              <Tooltip title={item.info as string}>
                <IconButton
                  size="small"
                  className={classes.itemActionButton}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  aria-label={`Info for ${item.name}`}
                >
                  <InfoOutlinedIcon style={{ fontSize: 15, color: 'orange' }} />
                </IconButton>
              </Tooltip>
            )}
            {onEdit && (
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  className={classes.itemActionButton}
                  onClick={e => { 
                    e.preventDefault();
                    e.stopPropagation(); 
                    onEdit(item); 
                  }}
                  aria-label={`Edit ${item.name}`}
                >
                  <EditIcon style={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  className={classes.itemActionButtonDelete}
                  onClick={e => { 
                    e.preventDefault();
                    e.stopPropagation(); 
                    onDelete(item); 
                  }}
                  aria-label={`Delete ${item.name}`}
                >
                  <DeleteIcon style={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
          </div>
        )}

        {item.isSystem && (
          <Tooltip title="System template — managed by Redview">
            <div className={classes.systemBadge}>S</div>
          </Tooltip>
        )}
      </div>
    );
  };

  return (
    <div className={classes.root}>

      {/* ── Header — hidden when no title is provided ── */}
      {title && (
        <div className={classes.templateHeader}>
          {onBack && (
            <>
              <Tooltip title={backTooltip} placement="bottom">
                <IconButton
                  className={classes.backButton}
                  size="small"
                  onClick={onBack}
                  aria-label={backTooltip}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <div className={classes.headerDivider} />
            </>
          )}
          {icon && (
            <Avatar className={classes.templateIcon} variant="rounded">
              {icon}
            </Avatar>
          )}
          <div className={classes.headerText}>
            <Typography className={classes.templateTitle}>{title}</Typography>
            {subtitle && (
              <Typography className={classes.templateSubtitle}>{subtitle}</Typography>
            )}
          </div>
          {onLearnMore && (
            <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', gap: 6 }}>
              <Button
                variant="text"
                size="small"
                onClick={onLearnMore}
                startIcon={<VideoLibraryOutlinedIcon style={{ fontSize: 14 }} />}
                className={classes.headerActionButton}
              >
                Learn More
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ── Filter bar — only when enableFilter is true ── */}
      {enableFilter && (
        <div className={classes.filterBar}>
          <span className={classes.filterLabel}>Filter</span>

          {filterChips.map(chip => {
            const isActive = activeFilters.has(chip.key);
            return (
              <Chip
                key={chip.key}
                label={chip.label}
                size="small"
                onClick={() => toggleFilter(chip.key)}
                onDelete={isActive ? () => toggleFilter(chip.key) : undefined}
                className={isActive ? classes.filterChipActive : classes.filterChip}
                icon={chip.icon as React.ReactElement | undefined}
              />
            );
          })}

          <div className={classes.filterDivider} />
          <div className={classes.filterSpacer} />

          {showSystemToggle && (
            <Chip
              label={
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <SecurityIcon style={{ fontSize: 13 }} />
                  {systemToggleLabel}
                </span>
              }
              size="small"
              onClick={() => setShowSystem(s => !s)}
              className={showSystem ? classes.systemToggleActive : classes.systemToggle}
            />
          )}
        </div>
      )}

      {/* ── Body ── */}
      <div className={classes.body}>

        {/* ── Sidebar ── */}
        <div 
          className={classes.sidebar}
          style={{ width: sidebarWidth }}
        >

          {/* Search row — when search is enabled, the create icon button sits next to it.
              When search is disabled but onCreate exists, a full-width create row is shown instead. */}
          {enableSearch ? (
            <div className={classes.sidebarSearch}>
              <div className={classes.searchWrap}>
                <SearchIcon className={classes.searchIcon} />
                <InputBase
                  className={classes.searchInput}
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  inputProps={{ 'aria-label': 'search sidebar' }}
                />
                {searchQuery && (
                  <ClearIcon
                    className={classes.clearIcon}
                    onClick={() => setSearchQuery('')}
                  />
                )}
              </div>
              {onCreate && (
                <Tooltip title={createLabel}>
                  <IconButton
                    size="medium"
                    className={classes.createIconButton}
                    onClick={onCreate}
                    aria-label={createLabel}
                    disabled={contentControl?.loading || sidebarLoading}
                  >
                    <AddIcon fontSize="medium" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          ) : (
            onCreate && (
              <div className={classes.sidebarCreateRow}>
                <div className={classes.createButton} onClick={onCreate}>
                  <AddIcon style={{ fontSize: 16 }} />
                  {createLabel}
                </div>
              </div>
            )
          )}

          {/* Item count — visible only when search is enabled */}
          {showCount && (
            <div className={classes.sidebarCount}>
              Showing {visibleItems.length} of {sidebarItems.length}
            </div>
          )}

          {/* List */}
          <div className={classes.sidebarList}>

            {sidebarLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px' }}>
                  <SkeletonLoader variant="shape" shape="rect" width={30} height={30} style={{ borderRadius: 8, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <SkeletonLoader variant="text" rows={1} widths={['60%']} heights={[14]} />
                  </div>
                </div>
              ))
            ) : (
              <>
            {groupedItems.map(({ group, items }) => (
              <div key={`group-${group.key}`}>
                <div className={classes.groupHeader}>
                  <span style={{ color: group.iconColor, fontSize: 13 }}>{group.icon}</span>
                  {group.label}
                </div>
                {items.map(renderItemRow)}
              </div>
            ))}

            {/* System group */}
            {systemItems.length > 0 && (
              <div>
                <div className={classes.groupHeader}>
                  <SecurityIcon className={classes.groupIcon} style={{ color: '#b45309' }} />
                  System
                </div>
                {systemItems.map(renderItemRow)}
              </div>
            )}

            {visibleItems.length === 0 && (
              <div className={classes.noResults}>
                No items match your search.
              </div>
            )}
              </>
            )}

          </div>
        </div>

        {/* ── Main content ── */}
        <div className={classes.main}>
          {hasContentControl && (
            <Paper square elevation={0} className={classes.contentControl}>
              {contentControl?.icon && !contentControl?.loading && (
                <div
                  className={classes.contentControlIconWrap}
                  style={{
                    backgroundColor: contentControl.iconBg ?? '#e3f2fd',
                    color: contentControl.iconColor ?? '#1565c0',
                  }}
                >
                  {contentControl.icon}
                </div>
              )}
              <div className={classes.contentControlText}>
                {contentControl?.loading ? (
                  <>
                    <SkeletonLoader
                      variant="text"
                      rows={1}
                      widths={['42%']}
                      heights={[14]}
                    />
                    <SkeletonLoader
                      variant="text"
                      rows={1}
                      widths={['28%']}
                      heights={[11]}
                    />
                  </>
                ) : (
                  <>
                    {contentControl?.title && (
                      <Typography className={classes.contentControlTitle}>
                        {contentControl.title}
                      </Typography>
                    )}
                    {contentControl?.subtitle && (
                      <Typography className={classes.contentControlSubtitle}>
                        {contentControl.subtitle}
                      </Typography>
                    )}
                  </>
                )}
              </div>
              {contentControl?.actions && (
                <div className={classes.contentControlAction}>
                  {contentControl.actions.onCreate && (
                    <Tooltip title={'Create new'}>
                      <IconButton
                        size="medium"
                        className={classes.contentControlIconBtn}
                        onClick={contentControl.actions.onCreate}
                        aria-label={'Create new'}
                        disabled={contentControl.loading}
                      >
                        <AddIcon fontSize="medium" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {contentControl.actions.onEdit && selectedItem && (
                    <Tooltip title="Edit">
                      <IconButton
                        size="medium"
                        className={classes.contentControlIconBtn}
                        onClick={() => contentControl.actions?.onEdit?.(selectedItem)}
                        aria-label={`Edit ${selectedItem.name}`}
                        disabled={contentControl.loading}
                      >
                        <EditIcon fontSize="medium" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {contentControl.actions.onDelete && selectedItem && (
                    <Tooltip title="Delete">
                      <IconButton
                        size="medium"
                        className={classes.contentControlIconBtn}
                        onClick={() => contentControl.actions?.onDelete?.(selectedItem)}
                        aria-label={`Delete ${selectedItem.name}`}
                        disabled={contentControl.loading}
                      >
                        <DeleteIcon fontSize="medium" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              )}
            </Paper>
          )}
          <div 
            className={classes.mainContent}
            style={{
               ...hasSharedComponents && {
                  padding: 0,
               }
            }}
          >
            <div 
              className={classes.contentCard}
              style={{
               ...hasSharedComponents && {
                  padding: 0,
                  border: 0,
                  boxShadow: 'none',
               }
            }}
            >
              {renderContent(selectedItem)}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};